import { useEffect, useMemo } from "react";

import {
  faInfoCircle,
  faShield,
  faTable,
  faTableLayout,
  faUser,
} from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { useWorkspaceUsers } from "src/api/taktile/queries";
import { Card } from "src/base-components/Card";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { Select } from "src/base-components/Select";
import { DEFAULT_STRING_LENGTH } from "src/base-components/schemaKit/validators";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import {
  CaseTemplateDropdown,
  LATEST_VERSION_ID,
} from "src/cases/common/CaseTemplateDropdown";
import { IconPicker } from "src/cases/common/IconPicker";
import { useCaseTemplatesWithVersions } from "src/cases/queries/templates";
import { CaseQueueFormValues } from "src/cases/queues/types";
import { CASE_QUEUE_ICONS, getCaseQueueIcon } from "src/cases/queues/utils";
import { Accordion } from "src/design-system/Accordion";
import { Avatar } from "src/design-system/Avatar";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { CaseConfigurationPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";
import { canUserBeAssignee, getUserName } from "src/utils/user";

const CaseQueueMembersField: React.FC<{
  isLoading?: boolean;
}> = ({ isLoading: isLoadingQueue }) => {
  const { formState } = useFormContext<CaseQueueFormValues>();
  const { orgId, wsId } = useParamsDecode<CaseConfigurationPageParams>();
  const { data: users = [], isLoading: isLoadingUsers } = useWorkspaceUsers(
    orgId,
    wsId,
    {
      include_deactivated: false,
    },
  );

  const assignees = users.filter((user) => canUserBeAssignee(wsId, user));

  const membersOptions = assignees.map((user) => ({
    key: user.id,
    searchText: getUserName(user),
    value: (
      <span className="flex items-center gap-x-2">
        <Avatar user={user} />
        {getUserName(user)}
      </span>
    ),
  }));

  return (
    <div className="flex flex-col">
      <Label>Select Members</Label>
      <Controller
        defaultValue={[]}
        name="memberIds"
        render={({ field }) => {
          return (
            <Select
              dataLoc="case-queue-members-select"
              disabled={isLoadingQueue || isLoadingUsers}
              dropdownPlaceholder={
                <EmptyState
                  description="No users matching your search. Try adjusting your search term."
                  headline="No users found"
                  icon={faUser}
                />
              }
              loading={isLoadingQueue || isLoadingUsers}
              options={membersOptions}
              placeholder="Members"
              value={field.value}
              multiple
              searchable
              showResetButton
              onChange={field.onChange}
            />
          );
        }}
      />
      {formState.errors.memberIds && (
        <ErrorHint>{formState.errors.memberIds.message}</ErrorHint>
      )}
    </div>
  );
};

const CaseQueueCaseTemplatesField: React.FC<{
  isLoading?: boolean;
}> = ({ isLoading: isLoadingQueue }) => {
  const { formState } = useFormContext<CaseQueueFormValues>();
  const { data: templates, isLoading: isLoadingTemplates } =
    useCaseTemplatesWithVersions();

  const caseTemplatesOptions =
    templates?.map((template) => ({
      key: template.id,
      searchText: template.attributes.template_name,
      value: (
        <span className="flex items-center gap-x-2">
          <Icon
            icon={getCaseQueueIcon(template.attributes.icon) ?? faTable}
            size="xs"
          />
          {template.attributes.template_name}
        </span>
      ),
    })) ?? [];

  return (
    <div className="flex flex-col">
      <Label>Select templates</Label>
      <Controller
        defaultValue={[]}
        name="templateIds"
        render={({ field }) => {
          return (
            <Select
              dataLoc="case-queue-case-templates-select"
              disabled={isLoadingQueue || isLoadingTemplates}
              dropdownPlaceholder={
                <EmptyState
                  description="No case templates matching your search. Try adjusting your search term."
                  headline="No case templates found"
                  icon={faTableLayout}
                />
              }
              loading={isLoadingQueue || isLoadingTemplates}
              options={caseTemplatesOptions}
              placeholder="Case templates"
              placement="top"
              value={field.value}
              multiple
              searchable
              showResetButton
              onChange={field.onChange}
            />
          );
        }}
      />
      {formState.errors.templateIds && (
        <ErrorHint>{formState.errors.templateIds.message}</ErrorHint>
      )}
    </div>
  );
};

const CaseQueueDefaultTemplateVersionField: React.FC<{
  isLoading?: boolean;
}> = ({ isLoading: isLoadingQueue }) => {
  const { setValue, getValues, control } =
    useFormContext<CaseQueueFormValues>();
  const templateIds = useWatch({ control, name: "templateIds" });

  const { data: templates, isLoading: isLoadingTemplates } =
    useCaseTemplatesWithVersions();

  const selectedTemplates = useMemo(
    () =>
      templates?.filter((template) => templateIds?.includes(template.id)) ?? [],
    [templates, templateIds],
  );

  const defaultTemplateVersion = useWatch({
    control,
    name: "defaultTemplateVersion",
  });

  const archivedVersionInfo = useMemo(() => {
    if (!defaultTemplateVersion) return null;
    if (defaultTemplateVersion.versionId === LATEST_VERSION_ID) return null;
    if (isLoadingTemplates) return null;

    const template = selectedTemplates.find(
      (t) => t.id === defaultTemplateVersion.templateId,
    );
    const version = template?.versions?.find(
      (v) => v.id === defaultTemplateVersion.versionId,
    );

    if (version && !version.attributes.archived) return null;

    return {
      templateName: template?.attributes.template_name,
      versionName: version?.attributes.version_name ?? (
        <TruncatedIdPill
          id={defaultTemplateVersion.versionId}
          variant="last-five"
        />
      ),
    };
  }, [defaultTemplateVersion, selectedTemplates, isLoadingTemplates]);

  const isSelectedVersionArchived = !!archivedVersionInfo;

  // Clear version selection when its template is removed from the multi-select
  useEffect(() => {
    const current = getValues("defaultTemplateVersion");
    if (current?.templateId && !templateIds?.includes(current.templateId)) {
      setValue("defaultTemplateVersion", null, { shouldDirty: true });
    }
  }, [templateIds, getValues, setValue]);

  if (!templateIds?.length) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <Label mb="mb-0">Default template for manual Cases</Label>
      <p className="mb-2 font-inter-normal-12px text-gray-500">
        Choose Case template and version used when reviewers create Cases
        manually from this queue. If not selected, manual Case creation will be
        disabled on this queue.
      </p>
      {isLoadingQueue || isLoadingTemplates ? (
        <Skeleton height="h-8" width="w-full" />
      ) : (
        <Controller
          control={control}
          name="defaultTemplateVersion"
          render={({ field }) => (
            <CaseTemplateDropdown
              dataLoc="case-queue-default-template-version"
              error={isSelectedVersionArchived}
              options={selectedTemplates}
              placeholder="Select default template version"
              selected={field.value ?? undefined}
              selectedTemplateName={archivedVersionInfo?.templateName}
              selectedVersionName={archivedVersionInfo?.versionName}
              publishedOnly
              showLatestVersionOption
              onReset={() => field.onChange(null)}
              onSelect={field.onChange}
            />
          )}
        />
      )}
      {isSelectedVersionArchived && (
        <ErrorHint>
          The selected template version has been archived. Please select another
          version.
        </ErrorHint>
      )}
    </div>
  );
};

export const CaseQueueConfiguration: React.FC<{
  isLoading?: boolean;
}> = ({ isLoading }) => {
  const form = useFormContext<CaseQueueFormValues>();
  const { queue_id } = useParamsDecode<CaseConfigurationPageParams>();
  const isEditing = !!queue_id;

  const isLoadingQueue = isEditing && isLoading;

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-col gap-y-2 border-gray-200 border-b p-5">
        <h2 className="font-inter-semibold-13px">Define queue settings</h2>
        <p className="font-inter-normal-12px text-gray-500">
          Provide basic details for the queue
        </p>
      </div>

      <div className="space-y-3 p-5">
        <Accordion
          defaultValue={[
            "queue-name",
            "set-membership",
            "select-case-template",
          ]}
        >
          <Accordion.Item label="Define the queue name" value="queue-name">
            <p className="font-inter-normal-12px text-gray-500">
              Define a name for this case queue.
            </p>
            <Card className="my-3 w-full">
              {isLoadingQueue ? (
                <div className="flex gap-x-2">
                  <Skeleton height="h-15" width="w-15" />
                  <div className="flex flex-1 flex-col gap-y-1.5">
                    <Skeleton height="h-4" width="w-full" />
                    <Skeleton height="h-4" width="w-110" />
                    <Skeleton height="h-4" width="w-90" />
                  </div>
                </div>
              ) : (
                <div className="flex gap-x-2">
                  <Controller
                    control={form.control}
                    defaultValue="shield-halved"
                    name="attributes.icon"
                    render={({ field }) => (
                      <IconPicker
                        dataLoc="case-queue-icon-picker"
                        options={CASE_QUEUE_ICONS}
                        placeholder={faShield}
                        size="md"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <div className="flex flex-1 flex-col">
                    <Label required>Name</Label>
                    <Input
                      data-loc="case-queue-name-input"
                      errored={!!form.formState.errors.attributes?.name}
                      placeholder="Queue name"
                      fullWidth
                      {...form.register("attributes.name", {
                        required: "Queue name is required",
                        validate: (value) =>
                          value.trim().length > 0 ||
                          "Queue name cannot be empty",
                        maxLength: {
                          value: DEFAULT_STRING_LENGTH,
                          message: `Queue name must not be more than ${DEFAULT_STRING_LENGTH} characters`,
                        },
                      })}
                    />
                    {form.formState.errors.attributes?.name && (
                      <ErrorHint dataLoc="case-queue-name-error-message">
                        {form.formState.errors.attributes.name.message}
                      </ErrorHint>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </Accordion.Item>
          <Accordion.Item label="Set membership" value="set-membership">
            <p className="font-inter-normal-12px text-gray-500">
              Select members to work on this queue. Only the members selected
              here can review cases in this queue.
            </p>
            <Card className="my-3 flex w-full flex-col gap-y-2">
              <CaseQueueMembersField isLoading={isLoadingQueue} />
              {isEditing && form.formState.dirtyFields.memberIds && (
                <Callout
                  dataLoc="case-queue-members-callout-warning"
                  type="warning"
                >
                  Cases assigned to removed members will not be automatically
                  unassigned and will remain linked to them. You must manually
                  unassign these cases if needed.
                </Callout>
              )}
            </Card>
            <p className="flex gap-x-1 font-inter-normal-12px text-gray-500">
              <Icon icon={faInfoCircle} size="xs" /> All eligible queue members
              can view and manually assign unassigned cases to themselves.
              Admins can reassign the cases to other queue members.
            </p>
          </Accordion.Item>
          <Accordion.Item
            label="Select case template"
            value="select-case-template"
          >
            <p className="font-inter-normal-12px text-gray-500">
              Select the case templates to include in this queue
            </p>
            <Card className="my-3 flex w-full flex-col gap-y-4">
              <CaseQueueCaseTemplatesField isLoading={isLoadingQueue} />
              {isEditing && form.formState.dirtyFields.templateIds && (
                <Callout
                  dataLoc="case-queue-case-templates-callout-warning"
                  type="warning"
                >
                  Cases with deleted templates will be marked as deleted and
                  will need to be manually archived if necessary. You can
                  continue to operate on them as usual.
                </Callout>
              )}
              <CaseQueueDefaultTemplateVersionField
                isLoading={isLoadingQueue}
              />
            </Card>
          </Accordion.Item>
        </Accordion>
      </div>
    </div>
  );
};
